import { useState } from "react";
import { Download, CheckCircle2, Info } from "lucide-react";

export default function Step3Download({ apiBaseUrl, jobId, onReset, onError }) {
  const [downloading, setDownloading] = useState(false);

  const handleDownload = async () => {
    if (!jobId) {
      onError("Missing job ID. Please restart the analysis.");
      return;
    }
    onError("");
    setDownloading(true);
    try {
      const res = await fetch(`${apiBaseUrl}/download/${jobId}`);
      if (!res.ok) {
        const text = await res.text().catch(() => "");
        throw new Error(text || `Download failed with status ${res.status}`);
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `ppc-optimiser-bulk-${jobId}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      onError(err?.message || "Download failed. Please try again.");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div
      className="box shadow-hard p-8 sm:p-14 text-center max-w-2xl mx-auto"
      data-testid="step3-root"
    >
      <div className="inline-flex items-center justify-center w-16 h-16 bg-[#0d0d0d] mb-6">
        <CheckCircle2 className="w-8 h-8 text-[#00c389]" strokeWidth={2} />
      </div>

      <p className="kicker justify-center mb-3">Done</p>
      <h2 className="display text-3xl sm:text-5xl text-[#0d0d0d] mb-5" data-testid="step3-heading">
        Your bulk file is ready
      </h2>
      <p className="text-sm text-[#565b57] max-w-md mx-auto mb-10 leading-relaxed">
        Upload this file in Seller Central under Advertising → Campaign Manager → Bulk Operations → Upload
      </p>

      <button
        type="button"
        onClick={handleDownload}
        disabled={downloading}
        className="btn-primary !text-base sm:!text-lg"
        data-testid="download-bulk-btn"
      >
        <Download className="w-5 h-5" />
        {downloading ? "Preparing download…" : "Download Bulk Upload File"}
      </button>

      {jobId && (
        <div className="mt-4 font-brand-mono text-[11px] text-[#565b57]">
          Having trouble?{" "}
          <a
            href={`${apiBaseUrl}/download/${jobId}`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-[#007d62] underline underline-offset-2 hover:text-[#0d0d0d] transition-colors"
            data-testid="download-direct-link"
          >
            Download directly in your browser
          </a>
        </div>
      )}

      <div
        className="mt-10 terminal border-l-4 border-l-[#00c389] p-5 flex items-start gap-3 text-left shadow-hard-sm"
        data-testid="download-reminder"
      >
        <Info className="w-5 h-5 text-[#00c389] shrink-0 mt-0.5" />
        <p className="text-sm text-white leading-relaxed">
          <span className="font-semibold">Remember:</span> review the file before uploading. New campaigns will go live immediately.
        </p>
      </div>

      <div className="mt-10">
        <button
          type="button"
          onClick={onReset}
          className="btn-secondary !text-xs uppercase tracking-wide"
          data-testid="reset-btn"
        >
          ← Optimise another account
        </button>
      </div>
    </div>
  );
}
